// EnhancedConsortiumDetail.jsx

import React, { useState } from 'react';
import {

  Menu,
  MenuItem,
  Divider,
  Container, Grid, Card, CardContent, Typography, Box, Avatar, Button, IconButton, Chip, Paper,
  CircularProgress, Alert, Tabs, Tab, Badge, List, ListItem, ListItemText, ListItemIcon, ListItemAvatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, TextField,
  Stack, Tooltip, Skeleton, LinearProgress,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  Person as PersonIcon, Email as EmailIcon, Business as BusinessIcon, Add as AddIcon, Edit as EditIcon,
  Campaign as CampaignIcon, Group as GroupIcon,
  TrendingUp as TrendingUpIcon, Assignment as AssignmentIcon, Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon, Star as StarIcon, Comment as CommentIcon,
  People as PeopleIcon, CalendarToday as CalendarTodayIcon, AccessTime as AccessTimeIcon,
  GroupAdd as GroupAddIcon, Delete as DeleteIcon, Visibility as VisibilityIcon,
  ExitToApp as ExitToAppIcon, InfoOutlined as InfoIcon, HowToReg as HowToRegIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import ConsortiumInvitationBanner from './ConsortiumInvitationBanner';
import { formatDistanceToNow, format } from 'date-fns';

// Shared helpers and API
import { axiosService, fetcher } from 'shared/helpers/axios';
import { useUser } from 'shared/helpers/axios';


// Components
import BackButton from 'components/common/buttons/BackButton';
// import BudgetWithJustifications from './BudgetWithJustifications';
import EditProposalDialog from './proposal/EditProposalDialog';
// import UserCard from 'components/collaboration/UserCard';
// import { getResearchArea, getAreaIcon } from 'components/collaboration/collaborationHelpers';
import { SuggestButton, JoinButton, InviteButton, MeetButton, MessageButton } from 'components/social/profile/ActionButtons';
import MeetingScheduleDialog from 'components/common/dialogs/MeetingScheduleDialog';
import InviteToSuggestEntityDialog from 'components/common/dialogs/InviteToSuggestEntityDialog';
import FollowButton from 'components/common/buttons/FollowButton';
import CreateProposalDialog from './components/CreateProposalDialog';
import CreatePublicationDialog from './publication/CreatePublicationDialog';
import { createFromOtableFile } from './publication/otableFile';
import AIIdeasDialog from 'components/collab/ai/AIIdeasDialog';
import { AutoAwesome as AIIdeasIcon } from '@mui/icons-material';
import ShareButton from 'components/common/buttons/ShareButton';
import ReportButton from 'components/common/ReportButton';
import EditConsortiumDialog from 'components/common/dialogs/EditConsortiumDialog';
import PendingInvitationsDialog from './pages/PendingInvitationsDialog';
import { ProposalStatusChip } from './EnhancedConsortiumList';
import { roleLabelFor, ROLE_ORDER, ROLE_RANK, WHITE_TEXT_ROLES, PROPOSAL_HIERARCHY, getRoleColors } from 'shared/constants/roleConstants';
// import ConsortiumSettingsDialog from 'components/common/dialogs/ConsortiumSettingsDialog';


import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import FlagIcon from '@mui/icons-material/Flag';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PageContainer, { PAGE_WIDTHS } from 'components/common/layout/PageContainer';
import { drawerWidth } from 'components/common/layout/Sidebar';

// This page keeps a small, nearly constant margin from the sidebar instead of centring (what centring would give
// with the sidebar open, capped at 40px), so it does not slide sideways when the sidebar collapses; the spare room
// goes to the right. The header, the tab bar and the content share this column.
const COLUMN_SX = {
  width: '100%', maxWidth: PAGE_WIDTHS.standard, boxSizing: 'border-box', mr: 'auto', px: { xs: 2, sm: 3 },
  ml: `clamp(0px, calc((100vw - ${drawerWidth}px - ${PAGE_WIDTHS.standard}px) / 2), 40px)`,
};





// Remove unused imports and commented imports for clarity

// Utility function to ensure data is an array
const ensureArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data === null || data === undefined) return [];
  if (typeof data === 'object') return Object.values(data);
  return [data];
};

const roleLabel = (role, publication = false) => roleLabelFor(role, { publication });

const InfoRow = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem', mb: 0.2 }}>
      {label}
    </Typography>
    <Typography variant="body2" color={value ? 'text.primary' : 'text.disabled'}>
      {value || '—'}
    </Typography>
  </Box>
);

const ROLE_AUTHORITIES = [
  {
    role: 'coordinator',
    label: 'Coordinator',
    desc: 'Administrative head of the consortium. Manages membership, assigns all roles, oversees every proposal and work package, and may transfer coordination to another member.',
  },
  {
    role: 'project_leader',
    label: 'Project Leader',
    desc: 'Leads a specific proposal. Organises work packages, assigns WP and Task Leaders, and submits the proposal on behalf of the consortium.',
  },
  {
    role: 'wp_leader',
    label: 'WP Leader',
    desc: 'Leads a work package within a proposal. Defines tasks, assigns Task Leaders, and is responsible for WP-level deliverables and milestones.',
  },
  {
    role: 'task_leader',
    label: 'Task Leader',
    desc: 'Leads a single task within a work package. Responsible for task execution, outputs, and reporting to the WP Leader.',
  },
  {
    role: 'researcher',
    label: 'Researcher',
    desc: 'Active consortium participant. Contributes to tasks, may be invited to proposals, and can follow consortium activity.',
  },
];

// Publications have no work packages; the project leader is the corresponding author
const PUBLICATION_AUTHORITIES = ROLE_AUTHORITIES
  .filter(({ role }) => !['wp_leader', 'task_leader'].includes(role))
  .map((r) => (r.role === 'project_leader' ? {
    ...r,
    label: 'Corresponding Author',
    desc: 'Leads a publication. Organises the writing, assigns sections and tasks, and corresponds with the journal on behalf of the authors.',
  } : r));

function RoleAuthorityTooltip({ publication = false }) {
  const theme = useTheme();
  const colors = getRoleColors(theme);
  const content = (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1, letterSpacing: 0.5, textTransform: 'uppercase', opacity: 0.75 }}>
        Role Authorities
      </Typography>
      {(publication ? PUBLICATION_AUTHORITIES : ROLE_AUTHORITIES).map(({ role, label, desc }) => (
        <Box key={role} sx={{ display: 'flex', gap: 1, mb: 1.25, alignItems: 'flex-start' }}>
          <Box sx={{
            mt: '3px', flexShrink: 0,
            width: 10, height: 10, borderRadius: '2px',
            backgroundColor: colors[role] ?? theme.palette.divider,
          }} />
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.3 }}>{label}</Typography>
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.82, lineHeight: 1.45 }}>{desc}</Typography>
          </Box>
        </Box>
      ))}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Box sx={{
          mt: '3px', flexShrink: 0,
          width: 10, height: 10, borderRadius: '2px',
          border: '1px solid', borderColor: 'divider',
        }} />
        <Box>
          <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.3 }}>External</Typography>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.82, lineHeight: 1.45 }}>
            Added to the consortium by another member but hasn't created or claimed an Otable account yet. They can't be messaged until they claim their membership.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
  return (
    <Tooltip title={content} placement="right" arrow
      componentsProps={{ tooltip: { sx: { bgcolor: 'background.paper', color: 'text.primary', boxShadow: 4, border: '1px solid', borderColor: 'divider', maxWidth: 340 } }, arrow: { sx: { color: 'background.paper' } } }}>
      <InfoIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help', flexShrink: 0, mt: '1px' }} />
    </Tooltip>
  );
}

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(225deg, #eff2f83a 0%, #dfe1e352 100%)',
  border: '1px solid #c9cbd052',
  transition: 'all 0.3s ease',
  '&:hover': { boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }
}));

function ClaimMembershipDialog({ membership, onClose, onSuccess }) {
  const [step, setStep] = useState('idle'); // idle | sent | success
  const [code, setCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const membershipId = membership.id;
  const hasClaimEmail = membership.member?.has_claim_email;

  const sendCode = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosService.post(`/consortium/memberships/${membershipId}/send-claim-code/`);
      setMaskedEmail(res.data.masked_email || '');
      setStep('sent');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setLoading(true);
    setError(null);
    try {
      await axiosService.post(`/consortium/memberships/${membershipId}/verify-claim-code/`, { code });
      setStep('success');
    } catch (e) {
      setError(e.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={step === 'success' ? onSuccess : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {step === 'success' ? 'Membership claimed!' : 'Is this you?'}
      </DialogTitle>
      <DialogContent>
        {step === 'success' ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Your Otable account has been linked to the <strong>{membership.member?.name}</strong> membership.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You see a member card for <strong>{membership.member?.name}</strong>
              {membership.organization?.name ? ` (${membership.organization.name})` : ''} still marked as external.
              If that's you, verify your identity to merge this membership with your account.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
            )}

            {!hasClaimEmail ? (
              <Alert severity="info">
                No email address is registered for this external profile. Ask your consortium coordinator to add your email to the membership first.
              </Alert>
            ) : step === 'idle' ? (
              <Typography variant="body2">
                A 6-digit verification code will be sent to the email your coordinator registered for this membership.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  A code was sent to <strong>{maskedEmail}</strong>. Enter it below.
                </Typography>
                <TextField
                  label="Verification code"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  size="small"
                  fullWidth
                  autoFocus
                />
                <Button
                  size="small"
                  onClick={sendCode}
                  disabled={loading}
                  sx={{ mt: 1, p: 0, textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Resend code
                </Button>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'success' ? (
          <Button variant="contained" onClick={onSuccess}>Done</Button>
        ) : step === 'idle' ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            {hasClaimEmail && (
              <Button variant="contained" onClick={sendCode} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : 'Send verification code'}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={verifyCode} disabled={loading || code.length !== 6}>
              {loading ? <CircularProgress size={18} /> : 'Verify'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

const MemberCard = ({ membership, isActive = true, currentUserId, onClaim, publication = false }) => {
  const navigate = useNavigate();
  const isExternal = membership.member?.is_external && membership.member?.is_active === false;
  const isSelf = currentUserId && String(membership.member?.id) === String(currentUserId);

  const handleCardClick = (e) => {
    if (isExternal) return;
    if (e.target.closest('button')) return;
    navigate(`/profile/${membership.member.id}`);
  };
  const theme = useTheme();
  return (

      <StyledCard
        sx={{ position: "relative", cursor: isExternal ? "default" : "pointer", ...(isActive ? {} : { opacity: 0.45, filter: 'grayscale(60%)' }) }}
        onClick={handleCardClick}
      >
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={membership.member?.avatar || undefined}
              alt={membership.member?.name || 'Member'}
              sx={{ width: 48, height: 48, mr: 1.5, fontSize: '1.5rem',
                backgroundColor: theme.palette.primary.avatar,
                color: 'white',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
               }}
            >
              {membership.member?.name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </Avatar>

                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {membership?.member?.name || "Unknown Member"}
                    </Typography>
                    {isExternal && (
                      <Chip
                        label="External"
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10, color: 'text.secondary', borderColor: 'divider' }}
                      />
                    )}
                  </Box>
                  {membership?.organization?.name ? (
                    <Typography variant="body2" color="text.secondary">
                      {membership.organization.name}
                    </Typography>
                  ) : !isExternal && (
                    isSelf ? (
                      <Typography
                        variant="body2"
                        sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={e => { e.stopPropagation(); navigate('/settings'); }}
                      >
                        Add Organization
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        Organization not set
                      </Typography>
                    )
                  )}

                  {ensureArray(membership?.roles).length > 0 && (
                    <Box mt={1}>
                      {(() => {
                        const ROLE_COLOR = getRoleColors(theme);
                        const allRoles = ensureArray(membership.roles);
                        const topProposalRole = PROPOSAL_HIERARCHY.find(r => allRoles.includes(r));
                        const sorted = allRoles
                          .filter(r => !PROPOSAL_HIERARCHY.includes(r) || r === topProposalRole)
                          .sort((a, b) => (ROLE_ORDER[a] ?? 99) - (ROLE_ORDER[b] ?? 99));
                        return sorted.map((role, i) => (
                          <Chip
                            key={i}
                            label={roleLabel(role, publication)}
                            size="small"
                            sx={{
                              mr: 0.5, mb: 0.5,
                              bgcolor: ROLE_COLOR[role] ?? theme.palette.primary.xulight,
                              color: WHITE_TEXT_ROLES.includes(role) ? '#fff' : 'inherit',
                            }}
                          />
                        ));
                      })()}
                    </Box>
                  )}
                </Box>

                {!isSelf && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                    {isExternal ? (
                      <Tooltip title="Is this you? Claim membership">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); onClaim(membership); }}>
                          <HowToRegIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Send direct message">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/messages/new?user=${membership.member.id}`); }}>
                          <MailOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              
              
              <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 0.4,
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        minWidth: 'fit-content',
                        minHeight: '50px',
                        maxHeight: '50px',
                        mt: 3, 
                        
                      }}
                    > 



{/* <Box key={membership.id}>

{(() => {
  const skills = membership?.member?.skills || [];
  const visibleSkills = skills.slice(0, 3);
  const extraCount = skills.length - visibleSkills.length;
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column',  gap: 0.4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {visibleSkills.map((exp) => (
        <Chip
          key={exp.id}
          label={exp.skill.name}
          sx={{ 
            height: 14, 
            transformOrigin: 'right center',
            bgcolor: theme.palette.primary.ulight,
            color: theme.palette.secondary.contrastText,
            '& .MuiChip-label': { 
              px: 0.6, 
              py: 0.4,
              fontSize: '0.9rem !important',
              fontWeight: '300',
              lineHeight: 1
            }
          }}
        />
      ))}

      {extraCount > 0 && (
        // <Chip label={`+${extraCount}`} size="small" />
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems:'center',  gap: 0.4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Typography variant="caption" color="text.secondary">
            {extraCount} more
          </Typography>
        </Box>


      )}
    </ Box>
  );
})()}

</Box> */}




              </Box>
              
          
          
          </Box>
        </CardContent>
      </StyledCard>

  );
};

const EnhancedConsortiumDetail = () => {
  const { consortiumId } = useParams();
  const navigate = useNavigate();
  const { user: me  }  = useUser();
  
  // console.log("**** me:", me);

  const [activeTab, setActiveTab] = useState(0);
  const [claimMembership, setClaimMembership] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);

  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [, setIsPending] = useState(false);

  // Meeting dialog state
  // const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  
  // Invite to consortium dialog state
  // const [inviteToConsortiumDialogOpen, setInviteToConsortiumDialogOpen] = useState(false);
  
  // Suggest entity dialog state
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);


  const [createProposalDialogOpen, setCreateProposalDialogOpen] = useState(false);
  const [createPublicationOpen, setCreatePublicationOpen] = useState(false);
  const [aiIdeasOpen, setAiIdeasOpen] = useState(false);
  const [proposalForm, setProposalForm] = useState({
    acronym: '',
    title: '',
    duration_months: '',
    call: null,
    language: 'en',
    ethics_flag: false,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [editProposalDialogOpen, setEditProposalDialogOpen] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [editConsortiumDialogOpen, setEditConsortiumDialogOpen] = useState(false);
  const [pendingInvitationsOpen, setPendingInvitationsOpen] = useState(false);
  // const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [deleteProposalDialogOpen, setDeleteProposalDialogOpen] = useState(false);
  const [deletingProposal, setDeletingProposal] = useState(null);
  const [transferPLDialogOpen, setTransferPLDialogOpen] = useState(false);
  const [transferPLProposal, setTransferPLProposal] = useState(null);
  const [transferPLMembershipId, setTransferPLMembershipId] = useState('');
  const [transferPLSubmitting, setTransferPLSubmitting] = useState(false);

  
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  
  const openActionsMenu = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };
  
  const closeActionsMenu = () => {
    setActionsAnchorEl(null);
  };



  // Consortium detail
  const { data: consortium, error, isLoading, mutate } = useSWR(
    consortiumId ? `/consortium/consortiums/${consortiumId}/` : null,
    fetcher,
    { revalidateOnFocus: false, revalidateOnReconnect: true, dedupingInterval: 30000 }
  );

  // console.log("0000 consortium: ", consortium);

  // Your consortiums (may return {results: [...]})
  const {
    data: userConsortiums = [],
    mutate: mutateUser
  } = useSWR(
    me ? '/consortium/consortiums/by-user/' : null,
    fetcher,
    {
      onError: (err) => console.warn('Failed to fetch user consortiums, using fallback data:', err?.message),
      fallbackData: [] // we still normalize in case API returns an object
    }
  );




// Proposals are embedded in the consortium detail response — no separate fetch needed.
const proposals = consortium?.proposals || [];
// Publication consortia write co-authored manuscripts instead of funding proposals
const isPublicationConsortium = consortium?.collaboration?.category === 'publication';
const docNoun = isPublicationConsortium ? 'publication' : 'proposal';

  // Loading state (detail)
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Error state (detail)
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load consortium details. Please try again.</Alert>
        <Button variant="contained" onClick={() => mutate()}>Retry</Button>
      </Container>
    );
  }

  // Not found
  if (!consortium) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>Consortium not found.</Alert>
        <Button variant="contained" onClick={() => navigate('/consortium/')}>Back to Consortiums</Button>
      </Container>
    );
  }

  // ===== Normalize IDs & permissions =====
  const currentUserId = String(me?.id ?? me?.pk ?? me?.public_id ?? '');

  // console.log("++++ consortium coordinator", consortium.coordinator_member); 
  const coordinatorMemberId = String(
    consortium?.coordinator_member?.member?.id ??
    consortium?.coordinator_member?.member?.pk ??
    consortium?.coordinator_member.member ?? ''
  );
  const isOwner = Boolean(currentUserId && coordinatorMemberId && currentUserId === coordinatorMemberId);

  // console.log("**** owner:", isOwner, { currentUserId, coordinatorMemberId });

  // ---- CRUCIAL FIX: normalize the userConsortiums response BEFORE using .map
  const userConsortiumList = Array.isArray(userConsortiums)
    ? userConsortiums
    : (userConsortiums?.results || userConsortiums?.items || []);

  const userConsortiumIdSet = new Set(
    userConsortiumList.map(c => String(c?.id ?? '')).filter(Boolean)
  );
  const userConsortiumPublicIdSet = new Set(
    userConsortiumList.map(c => String(c?.public_id ?? '')).filter(Boolean)
  );

  const thisId = String(consortium?.id ?? '');
  const thisPublicId = String(consortium?.public_id ?? '');

  const isMemberFromList =
    (thisId && userConsortiumIdSet.has(thisId)) ||
    (thisPublicId && userConsortiumPublicIdSet.has(thisPublicId));

  const isMemberFromDetail =
    ensureArray(consortium?.memberships).some((m) => {
      const memUserId = String(m?.member?.id ?? '');
      return memUserId && memUserId === currentUserId;
    });


  const isMemberPending = ensureArray(consortium?.join_requests).some((req) => {  
    const reqUserId = String(req?.requester?.id ?? '');
    return reqUserId && reqUserId === currentUserId && req.status === 'pending';
  });
    
  const isMember = isOwner || isMemberFromList || isMemberFromDetail;

  // Check if current user is a coordinator (member with coordinator role)
  const isCoordinator = ensureArray(consortium?.memberships).some((m) => {
    const memUserId = String(m?.member?.id ?? '');
    return memUserId && memUserId === currentUserId && m?.roles?.includes('coordinator');
  });

  const canInvite =
    isOwner ||
    isCoordinator || 
    (Array.isArray(consortium?.permissions) && consortium.permissions.includes('consortium.invite'));

  const memberCount = consortium?.member_count || 0;




    // Authorized roles: coordinator and wp_leader
    const userCollaborations = consortium.memberships || [];
    const userCollaboration = userCollaborations.find(collab =>
       String(collab.member?.id || collab.member?.pk || collab.member?.public_id || '') === currentUserId
    );
    const myMembershipId = userCollaboration ? String(userCollaboration.id) : null;

    const canEdit = userCollaboration?.roles.some(
       role => role === "coordinator" || role === "wp_leader" || role === "researcher"
    );


  const handleProposalClick = (proposal) => {
    // Check if user has permission to edit this proposal
      
    if (canEdit) {
      // Navigate to proposal editing page
      const segment = proposal.document_type === 'publication' ? 'publications' : 'proposals';
      navigate(`/consortium/${consortiumId}/${segment}/${proposal.id}/`);
    } else {
      setSnackbarMessage('You do not have permission to edit this proposal.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
    }
  };

  const handleCreateProposal = async () => {
    try {
      const payload = {
        consortium: consortium?.id,
        acronym: proposalForm.acronym,
        title: proposalForm.title,
        duration_months: proposalForm.duration_months || null,
        language: proposalForm.language || 'en',
        ethics_flag: proposalForm.ethics_flag ?? false,
        ...(proposalForm.call && { call: proposalForm.call }),
      };

      await axiosService.post('/proposal/proposals/', payload);

      setSnackbarMessage('Proposal created successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Reset form and close dialog
      setCreateProposalDialogOpen(false);
      setProposalForm({
        acronym: consortium?.name || '',
        title: '',
        duration_months: '',
        call: null,
        language: 'en',
        ethics_flag: false,
      });

      // Refresh proposals data
      mutate();
    } catch (err) {
      console.error('Error creating proposal:', err);
      setSnackbarMessage(`Failed to create proposal. ${err.response?.data?.error || err.message}. Please try again.`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };


  const handleCreatePublication = async (form) => {
    try {
      // form: title, target_journal, citation_style(_title), section_template, publication_type
      const res = await axiosService.post('/proposal/proposals/', {
        ...form,
        consortium: consortium?.id,
        document_type: 'publication',
      });
      setCreatePublicationOpen(false);
      mutate();
      navigate(`/consortium/${consortiumId}/publications/${res.data.id}/`);
    } catch (err) {
      console.error('Error creating publication:', err);
      setSnackbarMessage(`Failed to create publication. ${err.response?.data?.error || err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // a new publication / proposal from an Otable document (.otable), exported from Otable's writer;
  // errors are shown in the dialog (thrown back to it)
  const handleCreateFromOtableFile = async (file, documentType) => {
    const payload = documentType === 'publication'
      ? { consortium: consortium?.id, document_type: 'publication', citation_style: 'vancouver' }
      : { consortium: consortium?.id, acronym: proposalForm.acronym || consortium?.name || '', language: proposalForm.language || 'en',
          ...(proposalForm.call && { call: proposalForm.call }) };
    const { proposal, result } = await createFromOtableFile(file, payload);
    setCreatePublicationOpen(false);
    setCreateProposalDialogOpen(false);
    mutate();
    setSnackbarMessage(`Imported ${result.sections} section${result.sections === 1 ? '' : 's'}`
      + `${result.figures ? `, ${result.figures} figure${result.figures === 1 ? '' : 's'}` : ''}`
      + `${result.references ? ` and ${result.references} reference${result.references === 1 ? '' : 's'}` : ''}.`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    const segment = documentType === 'publication' ? 'publications' : 'proposals';
    navigate(`/consortium/${consortiumId}/${segment}/${proposal.id}/`);
  };

  const openCreateDocument = () => {
    if (isPublicationConsortium) {
      setCreatePublicationOpen(true);
      return;
    }
    setProposalForm(prev => ({ ...prev, acronym: prev.acronym || consortium?.name || '' }));
    setCreateProposalDialogOpen(true);
  };

  const handleJoinConsortium = async () => {
    setIsPending(true);
    try {
      // Create a join request for the consortium
      await axiosService.post('/interact/join-requests/', {
        consortium: consortiumId
      });
      
      setSnackbarMessage('Join request submitted successfully! The consortium coordinator will review your request.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      mutate();
      mutateUser();
    } catch (err) {
      console.error('Error submitting join request:', err);
      
      const errorMessage = err?.response?.data?.invitation 
        || err?.response?.data?.consortium
        || err?.response?.data?.message
        || err?.response?.data?.error
        || 'Failed to submit join request. Please try again.';
      
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleLeaveConsortium = async () => {
    setLeaving(true);
    try {
      await axiosService.delete(
        `/consortium/consortiums/${consortiumId}/remove-member/${userCollaboration?.id}/`
      );
      setSnackbarMessage('You have left the consortium.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setLeaveDialogOpen(false);
      mutate();
      mutateUser();
      setTimeout(() => navigate('/consortium/'), 1000);
    } catch (err) {
      setSnackbarMessage(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        'Failed to leave consortium.'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLeaving(false);
    }
  };

  const handleDeleteProposal = async () => {
    if (!deletingProposal) return;
    try {
      await axiosService.delete(`/proposal/proposals/${deletingProposal.id}/`);
      setSnackbarMessage('Proposal deleted successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      mutate();
    } catch (err) {
      setSnackbarMessage(
        err.response?.data?.detail || err.response?.data?.error || 'Failed to delete proposal.'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleteProposalDialogOpen(false);
      setDeletingProposal(null);
    }
  };

  const handleDeleteConsortium = async () => {
    try {
      await axiosService.delete(`/consortium/consortiums/${consortiumId}/`);
      setSnackbarMessage('Consortium deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      // Navigate back to consortium list after short delay
      setTimeout(() => {
        navigate('/consortium/');
      }, 1000);
    } catch (err) {
      console.error('Error deleting consortium:', err);
      setSnackbarMessage(
        err.response?.data?.detail || 
        err.response?.data?.message || 
        'Failed to delete consortium. Please try again.'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
    }
  };


  
 const handleMessage = () => {
    navigate(`/messages/new?consortium=${consortium.id}`);
  }  

  return (

    <Container maxWidth={false} disableGutters sx={{ mt: 0, mb: 4 }}>
      {/* invited here: accept or decline on the page itself */}
      <ConsortiumInvitationBanner consortiumId={consortium.id} consortiumName={consortium.name} onAnswered={() => mutate()} />
  {/* Header */}


  <Box mb={0} >
  <Paper 
    elevation={5}
    sx={(theme) => ({
      py: 2,
      pt: 5,
      borderRadius: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
      width: '100%',
      // background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      // position: 'relative',
      // overflow: 'hidden',
    })}
  >
    {/* Decorative accent */}
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        // background: consortium.status === 'active' 
        //   ? 'linear-gradient(90deg, #4caf50 0%, #81c784 100%)'
        //   : 'linear-gradient(90deg, #ff9800 0%, #ffb74d 100%)',
      }}
    />
    
    {/* Decorative corner */}
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 120,
        height: 120,
        // background: 'radial-gradient(circle at 100% 0%, rgba(25, 118, 210, 0.05) 0%, transparent 70%)',
        pointerEvents: 'none',
      }}
    />

    {/* Main content: the standard page width, so its left edge lines up with the tab content below */}
    <Box 
      display="flex" 
      justifyContent="space-between" 
      alignItems="flex-start"
      flexWrap="wrap"
      gap={2}
      sx={COLUMN_SX}
    >
      
      {/* Left side: Back button and consortium name */}
      <Box sx={{ 
        minWidth: 0, 
        flex: { xs: '1 1 100%', md: '1 1 0%' },
        maxWidth: { xs: '100%', md: '45%', lg: '50%' },
      }}>
   
          
        <Box display="flex" alignItems="center" gap={1.5} >
      
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">


            <BackButton position="inline" sx={{ width: 40, height: 40 }} />

              <Typography 
                variant="h3" 
                fontWeight="bold"
                sx={{ 
                  lineHeight: 1.2,
                  // background: 'linear-gradient(90deg, #1976d2 0%, #2196f3 100%)',
                  backgroundClip: 'text',
                  // WebkitBackgroundClip: 'text',
                  // WebkitTextFillColor: 'transparent',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: '100%', sm: '300px', md: '280px', lg: '400px' },
                }}
              >
                {consortium.name}
              </Typography>

     
              <ProposalStatusChip proposalStatus={consortium.proposal_status} />



{/* Action buttons row 2 */}
<Box display="flex" gap={1} alignItems="center" flexShrink={0}>

<FollowButton contentType="consortium" objectId={consortium.id} size="medium" variant="outlined" sx={{ flexShrink: 0 }}/>

</Box>

</Box>
            </Box>



            </Box>

            {/* Full description (if available) */}
    {/* {consortium.summary && (
     
        <Typography variant="body" color="text.secondary" sx={{ lineHeight: 1 }}>
        { consortium.summary.length > 120 ? consortium.summary.substring(0, 120) + '...' : consortium.summary }
        </Typography>
  
    )} */}
    

         {isMember && consortium.display_title && (
                  <Box sx={{ mt: 0, pt: 0.5, pl: 5 }}>
                    <Typography variant="body" color="text.secondary" sx={{ mt: 0.25 }}>
                      {consortium.display_title}
                    </Typography>
                  </Box>
                )}



            {/* Description summary */}
            {/* {consortium.objective && (
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  lineHeight: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontStyle: 'italic',
                }}
              >
                {consortium.objective}
              </Typography>
            )} */}



      </Box>

      {/* Center: Key metrics card */}
      <Box 
        sx={(theme) => ({ 
          mx: { xs: 0, md: 2, lg: 4 },
          px: { xs: 0, md: 2, lg: 4 },
          borderLeft: { xs: 'none', md: `1px solid ${theme.palette.divider}` },
          borderRight: { xs: 'none', md: `1px solid ${theme.palette.divider}` },
          borderTop: { xs: `1px solid ${theme.palette.divider}`, md: 'none' },
          display: 'flex',
          alignItems: 'center',
          flex: '0 0 auto',
          order: { xs: 3, md: 0 },
          width: { xs: '100%', md: 'auto' },
          justifyContent: { xs: 'center', md: 'flex-start' },
          pt: { xs: 2, md: 0 },
        })}
      >
        <Box sx={{ display: 'flex', gap: 4 }}>
          {/* Members count */}
          <Box sx={{ textAlign: 'center', minWidth: 100 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'grey.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1,
                border: '2px solid',
                borderColor: 'grey.100',
              }}
            >
              <PeopleIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            </Box>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {memberCount}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Members
            </Typography>
          </Box>

          {/* Creation date */}
          <Box sx={{ textAlign: 'center', minWidth: 100 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'grey.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1,
                border: '2px solid',
                borderColor: 'grey.100',
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: 20, color: 'success.main' }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" color="success.main">
              {new Date(consortium.created).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Created
            </Typography>
          </Box>

          {/* Duration */}
          {consortium.duration_months && (
            <Box sx={{ textAlign: 'center', minWidth: 100 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1,
                  border: '2px solid',
                  borderColor: 'grey.100',
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 28, color: 'warning.main' }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {consortium.duration_months}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Months
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right side: Action buttons */}
      <Stack 
        display="flex" 
        flexDirection="column" 
        alignItems={{ xs: 'center', md: 'flex-end' }}
        gap={5} 
        sx={{ 
          flex: { xs: '1 1 100%', md: '0 0 auto' },
          minWidth: { xs: 'auto', md: 200 },
          order: { xs: 2, md: 0 },
        }}
      >
        {/* Action buttons row 1 */}
        <Box display="flex" gap={1}>

        {!isMember && (
        
        <JoinButton  size="large" onClick={() => setJoinDialogOpen(true)} isPending={isMemberPending} />
      )}

          <SuggestButton size="large" onClick={() => setSuggestDialogOpen(true)} />

          {canInvite &&  
          <InviteButton size="large" onClick={() => setInviteDialogOpen(true)} /> }
          {isMember &&
          <MeetButton size="large" onClick={() => setMeetingDialogOpen(true)} /> }
          <MessageButton size="large" onClick={handleMessage} />



          <>
  <IconButton
    size="small"
    onClick={openActionsMenu}
    aria-label="Consortium Actions"
  >
    <MoreVertIcon />
  </IconButton>

  <Menu
    anchorEl={actionsAnchorEl}
    open={Boolean(actionsAnchorEl)}
    onClose={closeActionsMenu}
  >
    {/* Share */}
    <MenuItem
      onClick={() => {
        closeActionsMenu();
        // trigger share logic
      }}
      // sx={{ padding: 0 }} // Remove MenuItem padding
    >

  <ShareButton variant="dropdown" />

      {/* <ListItemIcon>
          <ShareButton />
      </ListItemIcon> */}
      {/* <ListItemText>Share</ListItemText> */}
    </MenuItem>

    {/* Report */}
    {!isOwner && (
      <MenuItem
        onClick={() => {
          closeActionsMenu();
          // open report dialog
        }}
      >
        <ListItemIcon>
          <FlagIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Report</ListItemText>
      </MenuItem>
    )}

    {(isOwner || isCoordinator) && <Divider />}

    {/* Owner / Coordinator actions */}
    {(isOwner || isCoordinator) && (
      <>
        <MenuItem
          onClick={() => {
            closeActionsMenu();
            setEditConsortiumDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Consortium</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeActionsMenu();
            setPendingInvitationsOpen(true);
          }}
        >
          <ListItemIcon>
            <MailOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Pending Invitations</ListItemText>
        </MenuItem>

        {/* <MenuItem
          onClick={() => {
            closeActionsMenu();
            setSettingsDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem> */}

        <MenuItem
          onClick={() => {
            closeActionsMenu();
            setDeleteDialogOpen(true);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Consortium</ListItemText>
        </MenuItem>
      </>
    )}

    {/* Member action */}
    {isMember && !isOwner && !isCoordinator && (
      <>
        <Divider />
        <MenuItem
          onClick={() => {
            closeActionsMenu();
            setLeaveDialogOpen(true);
          }}
          sx={{ color: 'warning.main' }}
        >
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText>Leave Consortium</ListItemText>
        </MenuItem>
      </>
    )}
  </Menu>
</>


        </Box>







      </Stack>
    </Box>


  </Paper>
</Box>


      {/* Tabs */}
      {  consortium.collaboration && (
      


      <>
        <Paper sx={{ borderRadius: 0 }}>

        <Stack flexDirection="row" justifyContent="space-between" alignItems="center"
          sx={COLUMN_SX}>

    
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          aria-label="consortium tabs"
          centered={!isMember} // Conditionally center only when not a member
          sx={{
            flexGrow: 1,
            minHeight: 48,
            // icon beside the label, not above it: a bar half as tall
            '& .MuiTab-root': { flexDirection: 'row', gap: 1, minHeight: 48, py: 1 },
            '& .MuiTab-iconWrapper': { mb: '0 !important' },
            '& .MuiTabs-scroller': {
              display: !isMember ? 'flex' : 'initial',
              justifyContent: !isMember ? 'center' : 'initial',
            }
          }}
>
        
        <Tab label="Overview" icon={<CampaignIcon />} />
          
          
          
        {/* Always render, conditionally hide */}
        <Tab 
          label="Members" 
          icon={
            <Badge badgeContent={isMember ? memberCount : 0}>
              <GroupIcon />
            </Badge>
          } 
          sx={{ display: isMember ? 'flex' : 'none' }}
        />
        
        <Tab 
          label={isPublicationConsortium ? 'Publications' : 'Proposals'} 
          icon={<AssignmentIcon />} 
          sx={{ display: isMember ? 'flex' : 'none' }}
        />
          {/* <Tab label="Details" icon={<AssignmentIcon />} /> */}
          {/* <Tab label="Call Information" icon={<CampaignIcon />} /> */}
          {/* <Tab label="Budget Planning" icon={<TrendingUpIcon />} /> */}
          {/* <Tab label="Analytics" icon={<TrendingUpIcon />} /> */}
        </Tabs>

        {canInvite && activeTab === 1 && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite Member
          </Button>
        )}
        {isMember && activeTab === 2 && (
          <Stack direction="row" spacing={1}>
            <Button startIcon={<AIIdeasIcon />} variant="outlined" onClick={() => setAiIdeasOpen(true)}>
              {isPublicationConsortium ? 'AI publication ideas' : 'AI project ideas'}
            </Button>
            {(isOwner || isCoordinator) && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={openCreateDocument}
              >
                {isPublicationConsortium ? 'Create Publication' : 'Create Proposal'}
              </Button>
            )}
          </Stack>
        )}


</Stack>
      </Paper>
      </>
 )}



      {/* Tab Content: the standard page width (the header and tab bars above span the window, their contents this width) */}
      <PageContainer sx={COLUMN_SX}>


      

      {activeTab === 0 && (
 <Box sx={{ minHeight: '400px', mt: 1 }}>
      {/* Overview */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <StyledCard sx={{minHeight:"300px"}}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <Typography variant="h6">Consortium Information</Typography>
                {consortium.summary && (
                  <Tooltip
                    title={
                      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 320, p: 1 }}>
                        <Typography variant="caption" sx={{ lineHeight: 1.6, fontSize: '1.3rem', whiteSpace: 'pre-wrap' }}>
                          {consortium.summary}
                        </Typography>
                        <Typography variant="caption" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap', mt: 1 }}>
                          {consortium.keywords && (
                            <p><strong>Keywords:</strong> {consortium.keywords.join(', ')}</p>
                          )}
                        </Typography>
                      </Box>
                    }
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: { sx: { bgcolor: 'background.paper', color: 'text.primary', boxShadow: 4, border: '1px solid', borderColor: 'divider', maxWidth: 320, p: 1.5 } },
                      arrow:   { sx: { color: 'background.paper' } },
                    }}
                  >
                    <InfoIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help', flexShrink: 0 }} />
                  </Tooltip>
                )}
              </Box>
              <List>
                <ListItem disablePadding sx={{ py: 1 }}>
                  <ListItemIcon><BusinessIcon /></ListItemIcon>
                  <ListItemText
                    primary="Coordinator Organization"
                    secondary={consortium.external_coordinator?.org_name || consortium.coordinator_org?.name || 'N/A'}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 1 }}>
                  <ListItemIcon><PersonIcon /></ListItemIcon>
                  <ListItemText
                    primary="Coordinator" 
                    secondary={consortium.external_coordinator?.name || (consortium?.coordinator_member ? consortium.coordinator_member_name ?? 'Unknown' : 'N/A')}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 1 }}>
                  <ListItemIcon><GroupIcon /></ListItemIcon>
                  <ListItemText primary="Members" secondary={`${memberCount} member${memberCount !== 1 ? 's' : ''}`} />
                </ListItem>
              </List>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledCard sx={{ cursor: 'pointer', minHeight:"300px" }} onClick={() => navigate(`/funding/call/${consortium?.funding_application?.call_details?.id}`)}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">Funding Information</Typography>
                {consortium?.funding_application?.call_details && (() => {
                  const cd = consortium.funding_application.call_details;
                  const statusLabel = cd.status === 'open' ? 'Open' : cd.status === 'closed' ? 'Closed' : cd.status || 'Unknown';
                  const statusColor = cd.status === 'open' ? 'success' : cd.status === 'closed' ? 'default' : 'warning';
                  return (
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Chip label={statusLabel} size="small" color={statusColor} variant="filled" />
                      {cd.total_budget ? (
                        <Chip label={`€${cd.total_budget.toLocaleString()}`} size="small" variant="outlined" />
                      ) : null}
                    </Box>
                  );
                })()}
              </Box>
              <List>
                <ListItem disablePadding sx={{ py: 1 }}>
                  <ListItemIcon><CampaignIcon /></ListItemIcon>
                  <ListItemText
                    primary="Related Call"
                    secondary={(() => {
                      const cd = consortium?.funding_application?.call_details;
                      if (!cd) return 'N/A';
                      const title = cd.title ? (cd.title.length > 65 ? cd.title.substring(0, 65) + '…' : cd.title) : 'N/A';
                      return cd.code ? `${cd.code} – ${title}` : title;
                    })()}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 1 }}>
                  <ListItemIcon><ScheduleIcon /></ListItemIcon>
                  <ListItemText primary="Program" secondary={consortium?.funding_application?.call_details?.program_name || 'N/A'} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 1 }}>
                  <ListItemIcon><TrendingUpIcon /></ListItemIcon>
                  <ListItemText primary="Agency" secondary={consortium?.funding_application?.call_details?.agency_name || 'N/A'} />
                </ListItem>
              </List>
            </CardContent>
          </StyledCard>
        </Grid>

        {proposals[0]?.created && (
          <Grid item xs={12} md={4}>
            <StyledCard sx={{ cursor: 'pointer', minHeight:"300px" }} onClick={(e) => { e.stopPropagation(); handleProposalClick(proposals[0]); }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Proposal Timeline</Typography>
                <List>
                  <ListItem disablePadding sx={{ py: 1 }}>
                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Started" secondary={new Date(proposals[0].created).toLocaleDateString()} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 1 }}>
                    <ListItemIcon><ScheduleIcon /></ListItemIcon>
                    <ListItemText primary="Number of Work Packages" secondary={`${proposals[0].wp_count ?? 'N/A'}`} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 1 }}>
                    <ListItemIcon><StarIcon /></ListItemIcon>
                    <ListItemText primary="Total Budget" secondary={`€${proposals[0].total_budget?.toLocaleString() || '0'}`} />
                  </ListItem>
                </List>
              </CardContent>
            </StyledCard>
          </Grid>
        )}

      </Grid>
     </Box>
      )}

      {/* Members Tab */}
      {activeTab === 1 && (() => {
        // Use the consortium's own last_proposal field — computed server-side from ALL proposals
        // regardless of what the current user can personally access.
        const lastProposal = consortium?.last_proposal || null;
        const lastProposalLeaderUserId = lastProposal?.project_leader_user_id
          ? String(lastProposal.project_leader_user_id) : null;
        // Membership IDs of members who explicitly left this specific proposal
        const leftMembershipIds = new Set(
          (lastProposal?.left_membership_ids || []).map(String)
        );

        // Augment memberships: inject project_leader role for the last proposal's leader only
        const rawMemberships = ensureArray(consortium?.memberships);
        let coordinatorAssigned = false;
        const augmented = rawMemberships.map(m => {
          const msId = String(m.id);
          const userId = String(m.member?.id ?? '');
          // Strip consortium-wide project_leader entries; only add it if this is the last proposal's leader
          const baseRoles = (m.roles || []).filter(r => r !== 'project_leader');
          const isThisProposalLeader = lastProposalLeaderUserId && userId === lastProposalLeaderUserId;
          const roles = isThisProposalLeader ? ['project_leader', ...baseRoles] : baseRoles;
          // Exactly one coordinator chip
          const isCoord = !coordinatorAssigned && roles.includes('coordinator');
          if (isCoord) coordinatorAssigned = true;
          const finalRoles = isCoord ? roles : roles.filter(r => r !== 'coordinator');
          // Inactive if the member explicitly left this proposal, or has no roles at all
          const hasLeftThisProposal = leftMembershipIds.has(msId);
          const isActive = !hasLeftThisProposal && (finalRoles.length > 0 || isThisProposalLeader);
          return { ...m, roles: finalRoles, _isActive: isActive };
        });

        const bestRoleRank = (m) =>
          (m.roles || []).reduce((best, r) => Math.max(best, ROLE_RANK[r] ?? -1), -1);

        const sorted = [...augmented].sort((a, b) => {
          // Inactive members always last
          const inactiveDiff = (a._isActive === false ? 1 : 0) - (b._isActive === false ? 1 : 0);
          if (inactiveDiff !== 0) return inactiveDiff;
          // Higher role rank first
          const rankDiff = bestRoleRank(b) - bestRoleRank(a);
          if (rankDiff !== 0) return rankDiff;
          // External members after non-external at the same rank
          const aExt = (a.member?.is_external && a.member?.is_active === false) ? 1 : 0;
          const bExt = (b.member?.is_external && b.member?.is_active === false) ? 1 : 0;
          return aExt - bExt;
        });

        return (
          <Box sx={{ minHeight: '100px', mt: 1 }}>
            {lastProposal && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>                
                <Typography variant="caption" color="text.secondary">
                  Members of <strong>{lastProposal.acronym || lastProposal.title || 'last proposal'}</strong>
                </Typography>
                <RoleAuthorityTooltip publication={isPublicationConsortium} />
              </Box>
            )}
            <Grid container spacing={2} mb={5}>
              {sorted.length > 0 ? sorted.map((membership) => (
                <Grid item key={membership.id} xs={12} sm={4} md={4}>
                  <MemberCard
                    membership={membership}
                    isActive={membership._isActive}
                    currentUserId={currentUserId}
                    onClaim={setClaimMembership}
                    publication={isPublicationConsortium}
                  />
                </Grid>
              )) : (
                <Alert severity="info">No members have joined this consortium yet.</Alert>
              )}
            </Grid>
          </Box>
        );
      })()}
              

              

            
      


     

     

      {/* Proposals Tab */}
      {activeTab === 2 && (

<Box sx={{ width: '100%', mt: 1, mb: 6 }}>

    <Box sx={{ pt: 1 }}>
      {isLoading ? (
        <Stack spacing={2}>
          {[0, 1].map(i => (
            <Box key={i} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Skeleton variant="rounded" width={80} height={22} />
                <Skeleton variant="rounded" width={90} height={22} />
              </Box>
              <Skeleton variant="text" width="55%" height={28} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
            </Box>
          ))}
        </Stack>
      ) : proposals.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...proposals]
            .sort((a, b) => new Date(b.updated || b.created) - new Date(a.updated || a.created))
            .map((proposal) => {
            const hasLeft = myMembershipId
              ? (proposal.left_membership_ids || []).some(id => String(id) === myMembershipId)
              : false;
            const canClick = canEdit && !hasLeft;

            return (
              <StyledCard
                key={proposal.id}
                sx={{ cursor: canClick ? 'pointer' : 'default', opacity: hasLeft ? 0.55 : 1, p: 3 }}
                onClick={() => canClick && handleProposalClick(proposal)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 700,
                        }}
                      >
                        {proposal.abbreviation || proposal.acronym || "N/A"}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {proposal.status && (
                          <Chip
                            label={proposal.status.replace('_', ' ')}
                            size="small"
                            variant="outlined"
                            color={
                              ['awarded', 'accepted', 'published'].includes(proposal.status) ? 'success' :
                              proposal.status === 'submitted'    ? 'primary' :
                              proposal.status === 'shortlisted'  ? 'info'    :
                              proposal.status === 'under_review' ? 'warning' :
                              proposal.status === 'rejected'     ? 'error'   :
                              proposal.status === 'withdrawn'    ? 'error'   : 'default'
                            }
                            sx={{ textTransform: 'capitalize' }}
                          />
                        )}
                        {hasLeft && (
                          <Chip label="Left" size="small" variant="outlined" color="default" />
                        )}
                        {canEdit && !hasLeft && (
                          <Chip
                            label="Can Edit"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>

                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                      {proposal.title}
                      
                    </Typography>

                    {proposal.abstract && (
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          lineHeight: 1.6,
                        }}
                      >
                        {proposal.abstract.length > 250 
                          ? `${proposal.abstract.slice(0, 250)}...` 
                          : proposal.abstract}
                      </Typography>
                    )}

                    {/* Project leader */}
                    {proposal.project_leader && (() => {
                      const plMembership = ensureArray(consortium?.memberships).find(
                        m => proposal.project_leader_user_id && String(m.member?.id) === String(proposal.project_leader_user_id)
                      );
                      const plOrg = plMembership?.organization?.name;
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                          <StarIcon sx={{ fontSize: 15, color: 'secondary.main' }} />
                          <Typography variant="body2" color="text.secondary">
                            Led by{' '}
                            <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
                              {proposal.project_leader}{plOrg ? ` (${plOrg})` : ''}
                            </Typography>
                          </Typography>
                        </Box>
                      );
                    })()}

                    {/* Call + Duration metadata */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
                      {proposal.call_title && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CampaignIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {proposal.call_code && (
                            <Chip label={proposal.call_code} size="small" variant="outlined"
                              sx={{ height: 18, fontSize: '0.95rem', borderRadius: '4px', flexShrink: 0 }} />
                          )}
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 500 }}>
                            {proposal.call_title}
                          </Typography>
                        </Box>
                      )}
                      {proposal.document_type === 'publication' && proposal.target_journal && (
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 500 }}>
                          → {proposal.target_journal}
                        </Typography>
                      )}
                      {proposal.duration_months && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {proposal.duration_months} months
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Tooltip title={format(new Date(proposal.updated), 'PPpp')} placement="top">
                        <Typography variant="body2" color="text.secondary" sx={{ cursor: 'default' }}>
                          📅 Last updated: {formatDistanceToNow(new Date(proposal.updated), { addSuffix: true })}
                        </Typography>
                      </Tooltip>
                      {/* {proposal.updated && proposal.updated !== proposal.created && (
                        <Tooltip title={format(new Date(proposal.updated), 'dd MMM yyyy, HH:mm')} placement="top">
                          <Typography variant="body2" color="text.secondary" sx={{ cursor: 'default' }}>
                            ↻ {format(new Date(proposal.updated), 'dd MMM yyyy, HH:mm')}
                          </Typography>
                        </Tooltip>
                      )} */}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* No project leader warning */}
                    {!proposal.project_leader && (
                      <Tooltip title={proposal.document_type === 'publication'
                        ? 'This publication has no corresponding author. Assign one before starting work.'
                        : 'This proposal has no project leader. Assign one before starting work.'}>
                        <Chip label={proposal.document_type === 'publication' ? 'No Corresponding Author' : 'No Project Leader'}
                          size="small" color="warning" variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 22 }} />
                      </Tooltip>
                    )}
                    {/* Transfer project leadership — visible to coordinator or current PL */}
                    {(isCoordinator || (proposal.project_leader_user_id && String(proposal.project_leader_user_id) === currentUserId)) && (
                      <Tooltip title={proposal.document_type === 'publication' ? 'Change corresponding author' : 'Transfer project leadership'}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTransferPLProposal(proposal);
                            setTransferPLMembershipId('');
                            setTransferPLDialogOpen(true);
                          }}
                          sx={{ color: 'secondary.main' }}
                        >
                          <StarIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingProposal(proposal);
                            setEditProposalDialogOpen(true);
                          }}
                          aria-label="Edit proposal"
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingProposal(proposal);
                            setDeleteProposalDialogOpen(true);
                          }}
                          aria-label="Delete proposal"
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              </StyledCard>
            );
          })}
        </Box>
      ) : (
        
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom color="text.secondary">
            No {docNoun}s created yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Start by creating the first {docNoun} for this consortium
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDocument}
            >
              {isPublicationConsortium ? 'Create Publication' : 'Create Proposal'}
            </Button>
            <Button variant="outlined" startIcon={<AIIdeasIcon />} onClick={() => setAiIdeasOpen(true)}>
              {isPublicationConsortium ? 'AI publication ideas' : 'AI project ideas'}
            </Button>
          </Stack>

        </Box>
      )}
    </Box>
  
</Box>




    )}


      {/* Details Tab */}
      {/* {activeTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>Consortium Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Basic Information</Typography>
                <List>
                  {/* <ListItem divider><ListItemText primary="Consortium ID" secondary={consortium.id} /></ListItem> 
                  {/* <ListItem divider><ListItemText primary="ID" secondary={consortium.id} /></ListItem>
                  <ListItem divider><ListItemText primary="Status" secondary={consortium.status_display} /></ListItem>
                  <ListItem divider><ListItemText primary="Duration" secondary={`${consortium.duration_months || 'N/A'} months`} /></ListItem>
                  <ListItem><ListItemText primary="Created" secondary={new Date(consortium.created).toLocaleString()} /></ListItem>
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Budget Information</Typography>
                <List>
                  <ListItem divider><ListItemText primary="Total Budget Requested" secondary={`€${consortium.total_budget_requested?.toLocaleString() || '0'}`} /></ListItem>
                  <ListItem divider><ListItemText primary="Own Budget" secondary={`€${consortium.total_budget_own?.toLocaleString() || '0'}`} /></ListItem>
                  <ListItem><ListItemText primary="Member Count" secondary={memberCount} /></ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )} */}

      {/* {activeTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>Related Funding Call</Typography>
          {consortium?.funding_application?.call_details ? (
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>{consortium?.funding_application?.call_details.title}</Typography>
                <Box display="flex" gap={2} mb={2}>
                  {consortium?.funding_application?.call_details.code && (
                    <Chip 
                      label={consortium?.funding_application?.call_details.code} 
                      variant="outlined"
                      onClick={() => navigate(`/funding/call/${consortium?.funding_application?.call_details.id}`)}
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                    />
                  )}
                  {consortium?.funding_application?.call_details.status && <Chip label={consortium?.funding_application?.call_details.status} color="primary" />}
                </Box>
                {consortium?.funding_application?.call_details.closes_at && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Closes: {new Date(consortium?.funding_application?.call_details.closes_at).toLocaleDateString()}
                  </Typography>
                )}
                {consortium?.funding_application?.call_details?.total_budget && (
                  <Typography variant="body1">Total Budget Available: €{Number(consortium?.funding_application?.call_details.total_budget).toLocaleString()}</Typography>
                )}
                {consortium?.funding_application?.call_details.funding_agency && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Agency: {consortium?.funding_application?.call_details.funding_agency.name}
                  </Typography>
                )}
                {consortium?.funding_application?.call_details.funding_program && (
                  <Typography variant="body2" color="text.secondary">
                    Program: {consortium?.funding_application?.call_details.funding_program.name}
                  </Typography>
                )}
              </CardContent>
            </StyledCard>
          ) : (
            <Alert severity="info">No funding call information available.</Alert>
          )}
        </Box>
      )} */}


      {/* {activeTab === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom>Budget Planning</Typography>
          {proposals.length > 0 ? (
            <BudgetPlanningTab consortium={consortium} proposal={proposals[0]} />
          ) : (
            <Alert severity="info">
              A proposal must be created first before budget planning can begin.
            </Alert>
          )}
        </Box>
      )} */}

      {/* {activeTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>Consortium Analytics</Typography>
          
          {ensureArray(consortium.memberships).length > 0 ? (
            <Grid container spacing={3}>
              {/* Basic Statistics 
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Member Statistics</Typography>
                    <List>
                      <ListItem>
                        <ListItemText primary="Total Members" secondary={ensureArray(consortium.memberships).length} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Coordinator" secondary={consortium?.coordinator_member?.member?.name || 'N/A'} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Research Partners" secondary={ensureArray(consortium.memberships).filter(m => ensureArray(m.roles)?.includes('researcher')).length} />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>

              {/* Budget Summary 
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Budget Summary</Typography>
                    <List>
                      <ListItem>
                        <ListItemText 
                          primary="Total Requested" 
                          secondary={`€${ensureArray(consortium.memberships).reduce((sum, m) => sum + (parseFloat(m.budget_req) || 0), 0).toLocaleString()}`} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Total Own Contribution" 
                          secondary={`€${ensureArray(consortium.memberships).reduce((sum, m) => sum + (m.budget_own || 0), 0).toLocaleString()}`} 
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">Loading member data...</Alert>
          )}
        </Box>
      )} */}

      {/* Invite Member Dialog */}


{ isMember && (
      <CreateProposalDialog
        createProposalDialogOpen={createProposalDialogOpen}
        setCreateProposalDialogOpen={setCreateProposalDialogOpen}
        proposalForm={proposalForm}
        setProposalForm={setProposalForm}
        handleCreateProposal={handleCreateProposal}
        consortium={consortium}
        existingProposals={proposals}
        handleCreateFromFile={(file) => handleCreateFromOtableFile(file, 'proposal')}
      />  
)}

{ isMember && consortium && (
      <AIIdeasDialog
        open={aiIdeasOpen}
        onClose={() => setAiIdeasOpen(false)}
        initialTab={isPublicationConsortium ? 'publications' : 'projects'}
        consortium={{ id: consortium.id, name: consortium.name }}
      />
)}

{ isMember && (
      <CreatePublicationDialog
        open={createPublicationOpen}
        onClose={() => setCreatePublicationOpen(false)}
        onCreate={handleCreatePublication}
        onCreateFromFile={(file) => handleCreateFromOtableFile(file, 'publication')}
        defaults={{
          title: consortium?.collaboration?.title,
          target_journal: consortium?.collaboration?.publication_details?.journal_name,
        }}
      />
)}

      {/* Leave Consortium Dialog */}
      <Dialog
        open={leaveDialogOpen}
        onClose={() => setLeaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1} color="warning.main">
            <ExitToAppIcon />
            Leave Consortium
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to leave <strong>"{consortium?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You will lose access to consortium resources and will need to request to rejoin.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveDialogOpen(false)} disabled={leaving}>Cancel</Button>
          <Button
            onClick={handleLeaveConsortium}
            variant="contained"
            color="warning"
            startIcon={leaving ? <CircularProgress size={16} color="inherit" /> : <ExitToAppIcon />}
            disabled={leaving}
          >
            Leave Consortium
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1} color="error.main">
            <DeleteIcon />
            Delete Consortium
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to delete the consortium <strong>"{consortium?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All associated data including memberships, proposals, and budgets will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConsortium}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Consortium
          </Button>
        </DialogActions>
      </Dialog>


    {/* Join Request Confirmation Dialog */}

      <Dialog
        open={joinDialogOpen}
        onClose={() => setJoinDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1} color="primary.main">
            <GroupAddIcon />
            Join Consortium
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to request to join the consortium <strong>"{consortium?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your request will be sent to the consortium coordinator for approval.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => { handleJoinConsortium(); setJoinDialogOpen(false); }}
            variant="contained"   
            color="primary"
          >
            Join
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Edit Proposal Dialog */}
      <EditProposalDialog
        open={editProposalDialogOpen}
        onClose={() => {
          setEditProposalDialogOpen(false);
          setEditingProposal(null);
        }}
        proposal={editingProposal}
        onSuccess={(updatedProposal) => {
          mutate(); // Refresh consortium (includes proposals + proposal_status badge)
          setEditProposalDialogOpen(false);
          setEditingProposal(null);
        }}
      />


{/* Meeting Schedule Dialog */}
      <MeetingScheduleDialog
        open={meetingDialogOpen}
        onClose={() => setMeetingDialogOpen(false)}
        restrictToUsers={ensureArray(consortium?.memberships).map(m => m.member).filter(Boolean)}
      />

        <InviteToSuggestEntityDialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          entityType="consortium"
          entity={consortium}
          currentUserId={currentUserId}
          mutate={mutate}
          mutateUser={mutateUser}
          mode="invite" // or "suggest"
        />


      <InviteToSuggestEntityDialog
          open={suggestDialogOpen}
          onClose={() => setSuggestDialogOpen(false)}
          entityType="consortium"
          entity={consortium}
          currentUserId={currentUserId}
          mutate={mutate}
          mutateUser={mutateUser}
          mode="suggest" // or "invite"
        />

      {/* Suggest Entity Dialog */}
      {/* <SuggestEntityDialog
        open={suggestDialogOpen}
        onClose={() => setSuggestDialogOpen(false)}
        user={me}
      /> */}

      {/* Edit Consortium Dialog */}
      <EditConsortiumDialog
        open={editConsortiumDialogOpen}
        onClose={() => setEditConsortiumDialogOpen(false)}
        consortium={consortium}
        onSuccess={() => {
          mutate(); // Refresh consortium data
          setSnackbarMessage('Consortium updated successfully');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }}
      />

      <PendingInvitationsDialog
        open={pendingInvitationsOpen}
        onClose={() => setPendingInvitationsOpen(false)}
        consortium={consortium}
      />

      {claimMembership && (
        <ClaimMembershipDialog
          membership={claimMembership}
          onClose={() => setClaimMembership(null)}
          onSuccess={() => { setClaimMembership(null); mutate(); }}
        />
      )}

      {/* Delete Proposal Warning Dialog */}
      <Dialog
        open={deleteProposalDialogOpen}
        onClose={() => { setDeleteProposalDialogOpen(false); setDeletingProposal(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1} color="error.main">
            <DeleteIcon />
            Delete {isPublicationConsortium ? 'Publication' : 'Proposal'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to delete the {docNoun} <strong>"{deletingProposal?.title || deletingProposal?.acronym}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isPublicationConsortium
              ? 'All sections, authors, references and project boards will be permanently removed.'
              : 'All associated work packages, tasks, and budget data will be permanently removed.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteProposalDialogOpen(false); setDeletingProposal(null); }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteProposal}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete {isPublicationConsortium ? 'Publication' : 'Proposal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Project Leadership Dialog */}
      <Dialog
        open={transferPLDialogOpen}
        onClose={() => { if (!transferPLSubmitting) { setTransferPLDialogOpen(false); setTransferPLProposal(null); } }}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 3100 }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon color="secondary" />
          {transferPLProposal?.document_type === 'publication' ? 'Change Corresponding Author' : 'Transfer Project Leadership'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select the member who will become the new {transferPLProposal?.document_type === 'publication' ? 'corresponding author' : 'Project Leader'} for{' '}
            <strong>{transferPLProposal?.acronym || transferPLProposal?.title || 'this proposal'}</strong>.
          </Typography>
          <List disablePadding>
            {ensureArray(consortium?.memberships)
              .filter(m => m.member?.id && String(m.member.id) !== String(transferPLProposal?.project_leader_user_id))
              .map(m => (
                <ListItem
                  key={m.id}
                  onClick={() => setTransferPLMembershipId(m.id)}
                  sx={{
                    borderRadius: 1, mb: 0.5, cursor: 'pointer',
                    border: '1px solid',
                    borderColor: transferPLMembershipId === m.id ? 'secondary.main' : 'divider',
                    bgcolor: transferPLMembershipId === m.id ? 'secondary.50' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={m.member?.avatar} sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
                      {(m.member?.name || '?').split(' ').map(p => p[0]).join('').slice(0,2).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={500}>{m.member?.name || 'Unknown'}</Typography>}
                    secondary={m.organization?.name}
                  />
                </ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTransferPLDialogOpen(false); setTransferPLProposal(null); }} disabled={transferPLSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!transferPLMembershipId || transferPLSubmitting}
            startIcon={transferPLSubmitting ? <CircularProgress size={16} color="inherit" /> : <StarIcon />}
            onClick={async () => {
              setTransferPLSubmitting(true);
              try {
                await axiosService.post('/consortium/member-roles/transfer_project_leader/', {
                  proposal_id: transferPLProposal.id,
                  consortium_membership_id: transferPLMembershipId,
                });
                mutate();
                setTransferPLDialogOpen(false);
                setTransferPLProposal(null);
                setSnackbarMessage('Project leadership transferred successfully.');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
              } catch (err) {
                setSnackbarMessage(err.response?.data?.error || 'Failed to transfer project leadership.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
              } finally {
                setTransferPLSubmitting(false);
              }
            }}
          >
            {transferPLSubmitting ? 'Transferring…' : 'Transfer Leadership'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Consortium Settings Dialog */}
      {/* <ConsortiumSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        consortium={consortium}
        onSuccess={() => {
          mutate(); // Refresh consortium data
          setSnackbarMessage('Settings updated successfully');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }}
      /> */}


          </PageContainer>
    </Container>
  );
};

export default EnhancedConsortiumDetail;
